import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AdminTable } from "./AdminTable";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", type: "live", description: "", long_description: "", duration: "", fees: 0, level: "Beginner", cover_image: "", modules: "", published: true };

export default function AdminCourses() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/courses").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, modules: (r.modules || []).join("\n") }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, fees: Number(form.fees), modules: form.modules.split("\n").map((m) => m.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/courses/${editing.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success(editing ? "Course updated" : "Course created");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete course "${r.title}"?`)) return;
    await api.delete(`/admin/courses/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-courses">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-4xl">Courses</h1><p className="text-foreground/60 mt-1">Manage live and recorded programs.</p></div>
        <Button data-testid="new-course-btn" onClick={openNew} className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold"><Plus className="w-4 h-4 mr-1" />New Course</Button>
      </div>

      <AdminTable
        testIdPrefix="course-row"
        columns={[
          { key: "title", label: "Title" },
          { key: "type", label: "Type" },
          { key: "level", label: "Level" },
          { key: "fees", label: "Fees", render: (r) => `₹${r.fees.toLocaleString()}` },
        ]}
        rows={rows}
        onEdit={openEdit}
        onDelete={del}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Course" : "New Course"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="live">Live</SelectItem><SelectItem value="recorded">Recorded</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Level</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Beginner", "Intermediate", "Advanced"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Duration</Label><Input required value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
              <div><Label>Fees (₹)</Label><Input type="number" required value={form.fees} onChange={(e) => setForm({ ...form, fees: e.target.value })} /></div>
            </div>
            <div><Label>Cover Image URL</Label><Input value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></div>
            <div><Label>Short Description</Label><Textarea rows={2} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Long Description</Label><Textarea rows={3} value={form.long_description || ""} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></div>
            <div><Label>Modules (one per line)</Label><Textarea rows={5} value={form.modules} onChange={(e) => setForm({ ...form, modules: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /><Label>Published</Label></div>
            <Button data-testid="save-course-btn" type="submit" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">{editing ? "Save Changes" : "Create Course"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
